import { useState, useEffect, useRef } from 'react';
import { apiFetch } from './shared';

const API = '/api';

function localDate(d = new Date()) {
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
}

function trendIcon(verlauf) {
  if (!verlauf || verlauf.length < 2) return '';
  const weights = verlauf.map(v => v.gewicht).filter(w => w > 0);
  if (weights.length < 2) return '';
  const latest = weights[0];
  const older = weights.slice(1, 4);
  const avgOlder = older.reduce((a, b) => a + b, 0) / older.length;
  const pct = (latest - avgOlder) / avgOlder * 100;
  if (pct > 4)  return { icon: '↑', col: '#4a9c6f' };
  if (pct > 1)  return { icon: '↗', col: '#7abf7a' };
  if (pct > -1) return { icon: '→', col: '#888' };
  if (pct > -4) return { icon: '↘', col: '#c17f24' };
  return { icon: '↓', col: '#cf7f50' };
}

// ─── Einzelner Satz mit ±0,5 kg Buttons ─────────────────────────────────────
function SetRow({ satz, idx, onUpdate, onDelete, einheit, flash, vorschlag, vorschlagPfeil }) {
  const gew = parseFloat(satz.gewicht) || 0;
  const flashColor = flash === 'saved' ? '#4a9c6f' : flash === 'error' ? '#cf7f50' : flash === 'adjusted' ? '#8a8adf' : flash === 'saving' ? '#666' : 'transparent';
  const zeigeVorschlag = vorschlag != null && vorschlag !== gew;

  return (
    <div style={{marginBottom:5}}>
    <div style={{display:'grid', gridTemplateColumns:'24px 1fr 1fr 1fr 24px', gap:4, alignItems:'center'}}>
      <div style={{fontSize:11, color:'#555', textAlign:'center', fontWeight:700}}>{idx+1}</div>

      {/* Gewicht mit ±0,5 Buttons */}
      <div style={{position:'relative', display:'flex', alignItems:'center', background:'#1a1a1a', border:'1px solid ' + (flash ? flashColor : '#2a2a2a'), borderRadius:4, overflow:'visible', transition:'border-color 0.3s'}}>
        {flash && (
          <span style={{position:'absolute', top:-6, right:-6, fontSize:10, color:flashColor, background:'#0a0a0a', borderRadius:'50%', width:14, height:14, display:'flex', alignItems:'center', justifyContent:'center', lineHeight:1}}>
            {flash === 'saved' ? '✓' : flash === 'error' ? '!' : flash === 'adjusted' ? '≈' : '·'}
          </span>
        )}
        <button
          onClick={() => onUpdate(idx, 'gewicht', String(Math.max(0, Math.round((gew - 0.5) * 10) / 10)))}
          style={{background:'transparent', border:'none', color:'#555', fontSize:14, padding:'0 5px', cursor:'pointer', fontFamily:'inherit', lineHeight:1, flexShrink:0}}
        >−</button>
        <input
          type="number" step="0.5" placeholder="kg"
          value={satz.gewicht}
          onChange={e => onUpdate(idx, 'gewicht', e.target.value)}
          style={{flex:1, minWidth:0, background:'transparent', border:'none', color:'#e0e0e0', fontSize:12, textAlign:'center', fontFamily:'inherit', outline:'none', padding:'6px 0'}}
        />
        <button
          onClick={() => onUpdate(idx, 'gewicht', String(Math.round((gew + 0.5) * 10) / 10))}
          style={{background:'transparent', border:'none', color:'#555', fontSize:14, padding:'0 5px', cursor:'pointer', fontFamily:'inherit', lineHeight:1, flexShrink:0}}
        >+</button>
      </div>

      {/* Wiederholungen / Sekunden */}
      <input
        type="number" step="1" placeholder={einheit === 's' ? 'sek' : 'Wdh'}
        value={satz.reps}
        onChange={e => onUpdate(idx, 'reps', e.target.value)}
        style={{background:'#1a1a1a', border:'1px solid #2a2a2a', borderRadius:4, padding:'6px 4px', color:'#e0e0e0', fontSize:12, fontFamily:'inherit', outline:'none', width:'100%', boxSizing:'border-box', textAlign:'center'}}
      />

      {/* RPE */}
      <select
        value={satz.rpe}
        onChange={e => onUpdate(idx, 'rpe', e.target.value)}
        style={{background:'#1a1a1a', border:'1px solid #2a2a2a', borderRadius:4, padding:'5px 2px', color: satz.rpe >= 9 ? '#cf7f50' : satz.rpe >= 7 ? '#c17f24' : '#666', fontSize:11, fontFamily:'inherit', outline:'none', width:'100%', boxSizing:'border-box'}}
      >
        <option value="">RPE</option>
        {[6,7,7.5,8,8.5,9,9.5,10].map(r => <option key={r} value={r}>{r}</option>)}
      </select>

      <button
        onClick={() => { if (window.confirm(`Satz ${idx + 1} wirklich löschen?`)) onDelete(idx); }}
        style={{background:'transparent', border:'none', color:'#3a1a1a', cursor:'pointer', fontSize:16, padding:0, lineHeight:1, textAlign:'center'}}
      >×</button>
    </div>

    {/* Gewichts-Vorschlag für den jeweils anderen Modus (Kraft↔MZ) — nur Anzeige,
        Übernahme ausschließlich über den Pfeil-Button (kein Autosave/Schreibzugriff).
        ↓ = Richtung MZ (leichter), ↑ = Kraft-Original wiederherstellen (schwerer) */}
    {zeigeVorschlag && (
      <div style={{display:'grid', gridTemplateColumns:'24px 1fr 1fr 1fr 24px', gap:4, alignItems:'center', marginTop:2}}>
        <div/>
        <div style={{display:'flex', alignItems:'center', gap:4}}>
          <span style={{fontSize:9, color:'#8a8adf'}}>Vorschlag: {vorschlag}kg</span>
          <button
            onClick={() => onUpdate(idx, 'gewicht', String(vorschlag))}
            title="Vorschlag übernehmen"
            style={{background:'transparent', border:'1px solid #333', borderRadius:3, color:'#8a8adf', fontSize:10, padding:'0 4px', cursor:'pointer', fontFamily:'inherit', lineHeight:1.5}}
          >{vorschlagPfeil || '↓'}</button>
        </div>
      </div>
    )}
    </div>
  );
}

// ─── Übungskarte ──────────────────────────────────────────────────────────────
function UebungCard({ uebung, trainingsZiel, zielDefaults, isSelected, onToggle, sets, onSetsChange, abgeschlossen, onAbschliessen, flashMap, onDeleteSatz, kraftOriginale }) {
  const empf = uebung.empfehlung;
  const last = uebung.letzteSession;
  const verlauf = uebung.verlauf || [];
  const trend = verlauf.length >= 2 ? trendIcon(verlauf) : null;

  // Gewichts-Vorschlag, reines Anzeige-Feld, nie ein Schreibzugriff.
  // MZ aktiv: nur solange das Feld noch exakt das beim Toggle gemerkte Kraft-Original
  // zeigt (kraftOriginale-Ref im Parent, in switchZiel() befüllt) — sonst würde ein
  // bereits reduziertes MZ-Gewicht (oder ein manuell geänderter Wert) fälschlich weiter
  // umgerechnet. Nach Übernahme des Vorschlags steht im Feld der neue (abweichende) Wert,
  // der Vergleich schlägt fehl und der Vorschlag verschwindet automatisch.
  // Kraft aktiv: das gemerkte Kraft-Original selbst als reiner Hinweis (kein Rechenschritt) —
  // nützlich nach einem MZ→Kraft-Rücktoggle, wenn im Feld noch der übernommene MZ-Wert steht.
  function berechneVorschlag(gewicht, idx) {
    const original = kraftOriginale[uebung.id + '-' + idx];
    if (original == null) return null;

    if (trainingsZiel === 'hypertrophie') {
      if (gewicht !== original) return null;
      const vr = uebung.gewicht_verhaeltnis;
      const gew = parseFloat(gewicht);
      if (!vr || !gew || gew <= 0 || !vr.kraft_zu_mz) return null;
      return Math.round(gew * vr.kraft_zu_mz / 2.5) * 2.5;
    }

    const orig = parseFloat(original);
    return orig > 0 ? orig : null;
  }

  const [sollSets, setSollSets] = useState(() => trainingsZiel === 'kraft' ? zielDefaults.kraft.sets : (uebung.default_sets || zielDefaults.hypertrophie.sets));
  const [sollReps, setSollReps] = useState(() => {
    if (uebung.einheit === 's') return uebung.default_reps || 30;
    return trainingsZiel === 'kraft' ? zielDefaults.kraft.reps : (uebung.default_reps || zielDefaults.hypertrophie.reps);
  });

  // Ergänzt fehlende Sätze ans Ende (Gewicht vom letzten Satz, Wdh aus Soll),
  // statt bestehende Sätze (inkl. ihrer DB-id) zu verwerfen und neu aufzubauen.
  function ergaenzteSets(anzahl, reps) {
    if (anzahl <= sets.length) return sets;
    const defaultGew = sets[sets.length - 1]?.gewicht || empf?.empfehlung || last?.gewicht || '';
    const neue = Array.from({ length: anzahl - sets.length }, () => ({
      gewicht: defaultGew ? String(defaultGew) : '',
      reps: String(reps),
      rpe: '',
    }));
    return [...sets, ...neue];
  }

  // Wie ergaenzteSets, gleicht zusätzlich die Wdh bereits vorhandener Zeilen an den
  // neuen Soll-Wert an (Gewicht bleibt unangetastet — siehe Vorschlag-Mechanik/
  // kraftOriginale). Ohne diesen Abgleich blieben Zeilen nach einem Kraft/MZ-Toggle
  // dauerhaft bei der Wdh-Zahl des vorherigen Modus stehen.
  function angleicheSetsAnZiel(anzahl, reps) {
    const angeglichen = sets.map(s => (String(s.reps) === String(reps) ? s : { ...s, reps: String(reps) }));
    if (anzahl <= angeglichen.length) return angeglichen;
    const defaultGew = angeglichen[angeglichen.length - 1]?.gewicht || empf?.empfehlung || last?.gewicht || '';
    const neue = Array.from({ length: anzahl - angeglichen.length }, () => ({
      gewicht: defaultGew ? String(defaultGew) : '',
      reps: String(reps),
      rpe: '',
    }));
    return [...angeglichen, ...neue];
  }

  useEffect(() => {
    let zielSets, zielReps;
    if (trainingsZiel === 'kraft') {
      zielSets = zielDefaults.kraft.sets;
      zielReps = uebung.einheit !== 's' ? zielDefaults.kraft.reps : sollReps;
    } else {
      zielSets = uebung.default_sets || zielDefaults.hypertrophie.sets;
      zielReps = uebung.default_reps || zielDefaults.hypertrophie.reps;
    }
    setSollSets(zielSets);
    setSollReps(zielReps);
    if (!isSelected) return;

    // Kraft/MZ-Toggle (oder eine geänderte Soll-Vorgabe): fehlende Sätze ergänzen UND
    // die Wdh bereits vorhandener Zeilen an den neuen Soll-Wert angleichen — sonst
    // zeigen alte Zeilen nach dem Toggle weiterhin die Wdh des vorherigen Modus.
    const aktualisiert = angleicheSetsAnZiel(zielSets, zielReps);
    const veraendert = aktualisiert.length !== sets.length || aktualisiert.some((s, i) => s !== sets[i]);
    if (!veraendert) return;

    onSetsChange(aktualisiert);
    // Bereits gespeicherte Zeilen (mit DB-id), deren Wdh sich geändert hat, direkt
    // nachsynchronisieren — sonst driftet die DB bis zum nächsten manuellen Edit
    // dieser Zeile von der Anzeige weg.
    aktualisiert.forEach((s, i) => {
      if (sets[i] && s !== sets[i] && sets[i].id) onSetsChange(aktualisiert, i);
    });
  }, [trainingsZiel, zielDefaults]); // eslint-disable-line

  // Sätze aus einer offenen Session laden asynchron nach (GET /gym/offene-session),
  // lange nachdem der obige Effekt schon mit dem Default-Wert gelaufen ist. Sobald
  // die tatsächliche Satzanzahl bekannt ist (sets.length ändert sich), den
  // Soll-Zähler danach ausrichten statt beim Default-Wert zu bleiben.
  useEffect(() => {
    if (sets.length > 0 && sets.length !== sollSets) {
      setSollSets(sets.length);
    }
  }, [sets.length]); // eslint-disable-line

  function buildSets(anzahl, reps) {
    const defaultGew = empf?.empfehlung || last?.gewicht || '';
    return Array.from({ length: anzahl }, () => ({
      gewicht: defaultGew ? String(defaultGew) : '',
      reps: String(reps),
      rpe: '',
    }));
  }

  function handleToggle() {
    onToggle();
    if (!isSelected && sets.length === 0) {
      onSetsChange(buildSets(sollSets, sollReps));
    }
  }

  function applySollAenderung(neuAnzahl, neuReps) {
    // Reduzierung: überzählige Sätze wirklich entfernen (inkl. DB-Löschung über
    // onDeleteSatz), statt sie nur durch ein frisches Array ohne ihre id zu
    // ersetzen — sonst bleiben sie als verwaiste kraft_saetze-Zeilen bestehen
    // und tauchen beim nächsten Laden der Session wieder auf.
    if (neuAnzahl < sets.length) {
      const entfernt = sets.slice(neuAnzahl);
      const hatGewicht = entfernt.some(s => parseFloat(s.gewicht) > 0);
      if (hatGewicht) {
        const ok = window.confirm(
          `${entfernt.length} Satz${entfernt.length > 1 ? 'e' : ''} mit bereits eingetragenem Gewicht wird entfernt. Fortfahren?`
        );
        if (!ok) return;
      }
      setSollSets(neuAnzahl);
      setSollReps(neuReps);
      onSetsChange(sets.slice(0, neuAnzahl));
      entfernt.forEach(s => { if (s.id) onDeleteSatz(s.id); });
      return;
    }

    setSollSets(neuAnzahl);
    setSollReps(neuReps);
    // Erhöhung: fehlende Sätze ans Ende ergänzen (Gewicht vom letzten Satz,
    // Wdh aus Soll) statt alles neu aufzubauen — sonst gehen die bereits
    // vorhandenen Sätze (inkl. DB-id und eingetragenen Werten) verloren.
    if (isSelected && neuAnzahl > sets.length) {
      onSetsChange(ergaenzteSets(neuAnzahl, neuReps));
    }
  }

  function addSet() {
    const lastSet = sets[sets.length - 1];
    const defaultGew = lastSet?.gewicht || empf?.empfehlung || last?.gewicht || '';
    onSetsChange([...sets, { gewicht: defaultGew ? String(defaultGew) : '', reps: String(sollReps), rpe: '' }]);
  }

  function updateSet(idx, field, val) {
    onSetsChange(sets.map((s, i) => i === idx ? { ...s, [field]: val } : s), idx);
  }

  function deleteSet(idx) {
    const satz = sets[idx];
    onSetsChange(sets.filter((_, i) => i !== idx));
    if (satz?.id) onDeleteSatz(satz.id);
  }

  const tageHer = last?.tage_her;
  const ruheWarnung = tageHer != null && tageHer < 2;

  // Letztes Training: formatierte Anzeige
  let letzteInfo = '';
  if (last) {
    const parts = [];
    if (last.gewicht) parts.push(last.gewicht + 'kg');
    if (last.saetze || last.logged_sets) {
      const s = last.logged_sets > 0 ? last.logged_sets : last.saetze;
      const r = last.wdh;
      parts.push(s + (r ? '×' + r : ' Sätze'));
    }
    if (last.avg_rpe) parts.push('RPE ' + last.avg_rpe);
    letzteInfo = parts.join(' · ');
  }

  return (
    <div style={{borderBottom:'1px solid #1c1c1c'}}>
      {/* ── Header: Checkbox + Name + Meta ── */}
      <div
        onClick={handleToggle}
        style={{display:'flex', alignItems:'center', padding:'12px 16px', cursor:'pointer', background: abgeschlossen ? '#0d1f10' : isSelected ? '#0d1a0d' : 'transparent', userSelect:'none'}}
      >
        <div style={{
          width:20, height:20, borderRadius:4, flexShrink:0, marginRight:12,
          border:'2px solid ' + (isSelected ? '#4a9c6f' : '#333'),
          background: isSelected ? '#4a9c6f' : 'transparent',
          display:'flex', alignItems:'center', justifyContent:'center',
        }}>
          {isSelected && <span style={{color:'#fff', fontSize:12, fontWeight:700}}>✓</span>}
        </div>

        <div style={{flex:1, minWidth:0}}>
          <div style={{display:'flex', alignItems:'center', gap:6}}>
            <span style={{fontSize:13, fontWeight:600, color: isSelected ? '#a0d0a0' : '#e0e0e0'}}>{uebung.name}</span>
            {abgeschlossen && <span style={{fontSize:12, color:'#4a9c6f', fontWeight:700}}>✓</span>}
            {trend && <span style={{fontSize:12, color: trend.col, fontWeight:700}}>{trend.icon}</span>}
            {ruheWarnung && <span style={{fontSize:10, color:'#c17f24'}}>⚠ {'<'}48h</span>}
          </div>
          {uebung.notiz && <div style={{fontSize:10, color:'#555', marginTop:1}}>{uebung.notiz}</div>}
          {last && (
            <div style={{fontSize:10, marginTop:2}}>
              <span style={{color: ruheWarnung ? '#c17f24' : '#3a3a3a'}}>{tageHer}d her</span>
              {letzteInfo && <span style={{color:'#444'}}> · {letzteInfo}</span>}
            </div>
          )}
        </div>

        {/* Rechte Seite: Empfehlung */}
        {empf && (
          <div style={{flexShrink:0, textAlign:'right', marginLeft:8}}>
            <div style={{fontSize:13, fontWeight:700, color: empf.deload ? '#cf7f50' : empf.steigern ? '#4a9c6f' : '#666'}}>
              {empf.empfehlung}kg {empf.steigern ? '↑' : empf.deload ? '↓' : ''}
            </div>
            <div style={{fontSize:9, color:'#3a3a3a', maxWidth:100, textAlign:'right', lineHeight:1.3}}>{empf.hinweis}</div>
          </div>
        )}

        {isSelected && (
          <button
            onClick={e => { e.stopPropagation(); onAbschliessen(); }}
            title={abgeschlossen ? 'Als offen markieren' : 'Übung abschließen'}
            style={{
              marginLeft:8, flexShrink:0, width:26, height:26, borderRadius:'50%', padding:0,
              border:'2px solid ' + (abgeschlossen ? '#4a9c6f' : '#333'),
              background: abgeschlossen ? '#4a9c6f' : 'transparent',
              color: abgeschlossen ? '#fff' : '#555',
              fontSize:13, fontWeight:700, cursor:'pointer', fontFamily:'inherit',
              display:'flex', alignItems:'center', justifyContent:'center',
            }}
          >✓</button>
        )}
      </div>

      {/* ── Soll-Sets/Reps + Satz-Logging (wenn ausgewählt) ── */}
      {isSelected && (
        <div style={{padding:'8px 16px 12px 48px', background:'#080e08'}}>

          {/* Soll-Vorgaben editierbar */}
          <div style={{display:'flex', gap:8, alignItems:'center', marginBottom:10}}>
            <span style={{fontSize:10, color:'#444', whiteSpace:'nowrap'}}>Soll:</span>
            <div style={{display:'flex', alignItems:'center', background:'#111', border:'1px solid #222', borderRadius:4, overflow:'hidden'}}>
              <button onClick={() => applySollAenderung(Math.max(1, sollSets - 1), sollReps)}
                style={{background:'transparent', border:'none', color:'#555', padding:'3px 7px', cursor:'pointer', fontFamily:'inherit', fontSize:13}}>−</button>
              <span style={{fontSize:12, color:'#aaa', padding:'3px 4px', minWidth:16, textAlign:'center'}}>{sollSets}</span>
              <button onClick={() => applySollAenderung(Math.min(8, sollSets + 1), sollReps)}
                style={{background:'transparent', border:'none', color:'#555', padding:'3px 7px', cursor:'pointer', fontFamily:'inherit', fontSize:13}}>+</button>
            </div>
            <span style={{fontSize:10, color:'#444'}}>Sätze ×</span>
            <div style={{display:'flex', alignItems:'center', background:'#111', border:'1px solid #222', borderRadius:4, overflow:'hidden'}}>
              <button onClick={() => applySollAenderung(sollSets, Math.max(1, sollReps - 1))}
                style={{background:'transparent', border:'none', color:'#555', padding:'3px 7px', cursor:'pointer', fontFamily:'inherit', fontSize:13}}>−</button>
              <span style={{fontSize:12, color:'#aaa', padding:'3px 4px', minWidth:20, textAlign:'center'}}>{sollReps}</span>
              <button onClick={() => applySollAenderung(sollSets, Math.min(30, sollReps + 1))}
                style={{background:'transparent', border:'none', color:'#555', padding:'3px 7px', cursor:'pointer', fontFamily:'inherit', fontSize:13}}>+</button>
            </div>
            <span style={{fontSize:10, color:'#444'}}>{uebung.einheit === 's' ? 'sek' : 'Wdh'}</span>
            {uebung.einheit !== 's' && (
              <span style={{fontSize:9, color: trainingsZiel === 'kraft' ? '#7a7abf' : '#3a7a5a', marginLeft:2}}>
                {trainingsZiel === 'kraft' ? '~85% 1RM' : '~70% 1RM'}
              </span>
            )}
          </div>

          {/* Spalten-Header */}
          <div style={{display:'grid', gridTemplateColumns:'24px 1fr 1fr 1fr 24px', gap:4, marginBottom:4}}>
            <div/>
            <div style={{fontSize:9, color:'#444', textAlign:'center'}}>Gewicht</div>
            <div style={{fontSize:9, color:'#444', textAlign:'center'}}>{uebung.einheit === 's' ? 'Sek' : 'Wdh'}</div>
            <div style={{fontSize:9, color:'#444', textAlign:'center'}}>RPE</div>
            <div/>
          </div>

          {/* Sätze */}
          {sets.map((s, i) => (
            <SetRow key={i} satz={s} idx={i} onUpdate={updateSet} onDelete={deleteSet} einheit={uebung.einheit} flash={flashMap?.[uebung.id + '-' + i]} vorschlag={berechneVorschlag(s.gewicht, i)} vorschlagPfeil={trainingsZiel === 'kraft' ? '↑' : '↓'}/>
          ))}

          <button
            onClick={e => { e.stopPropagation(); addSet(); }}
            style={{marginTop:4, background:'transparent', border:'1px dashed #2a3a2a', borderRadius:4, color:'#4a7c59', fontSize:11, padding:'4px 12px', cursor:'pointer', fontFamily:'inherit', width:'100%'}}
          >+ Satz hinzufügen</button>
        </div>
      )}
    </div>
  );
}

// ─── Muskelgruppen-Balance ────────────────────────────────────────────────────
function MuskelBalance({ balance }) {
  if (!balance || balance.length === 0) return null;
  const uk = balance.find(b => b.gruppe === 'gym_a');
  const ok = balance.find(b => b.gruppe === 'gym_b');
  const es = balance.find(b => b.gruppe === 'gym_c');
  const esN = es?.count || 0;
  // Essentials zählt als eigene Ganzkörper-Kategorie, nicht als zusätzliche UK-/OK-Session
  const ukN = uk?.count || 0;
  const okN = ok?.count || 0;
  // Freshness (Tage her) profitiert trotzdem von Essentials, da es beide Bereiche mittrainiert
  const frischer = (a, b) => a == null ? b : b == null ? a : Math.min(a, b);
  const total = Math.max(ukN, okN, esN, 1);

  return (
    <div style={{background:'#141414', borderRadius:8, padding:'12px 16px', marginBottom:12}}>
      <div style={{fontSize:10, color:'#555', marginBottom:8, letterSpacing:2}}>BALANCE LETZTE 14 TAGE</div>
      {[
        { label:'Unterkörper', n:ukN, tage:frischer(uk?.tage_her, es?.tage_her), col:'#4a7c59' },
        { label:'Oberkörper',  n:okN, tage:frischer(ok?.tage_her, es?.tage_her), col:'#2a6fa8' },
        { label:'Essentials',  n:esN, tage:es?.tage_her, col:'#a87c2a' },
      ].map(({ label, n, tage, col }) => (
        <div key={label} style={{display:'flex', gap:8, alignItems:'center', marginBottom:6}}>
          <div style={{fontSize:11, color: col, width:84}}>{label}</div>
          <div style={{flex:1, background:'#1a1a1a', borderRadius:3, height:8, overflow:'hidden'}}>
            <div style={{width: (n/total*100)+'%', height:'100%', background: col, borderRadius:3, minWidth: n > 0 ? 4 : 0}}/>
          </div>
          <div style={{fontSize:10, color: col, width:22, textAlign:'right'}}>{n}×</div>
          {tage != null && <div style={{fontSize:10, color:'#444', width:30}}>{tage}d</div>}
        </div>
      ))}
    </div>
  );
}

// ─── Hauptkomponente ──────────────────────────────────────────────────────────
export default function GymSession({ onClose }) {
  const [uebungen, setUebungen] = useState([]);
  const [gruppenTab, setGruppenTab] = useState('unterkörper');
  const [selected, setSelected] = useState(new Set());
  const [sets, setSets] = useState({});
  const [aiEmpf, setAiEmpf] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [balance, setBalance] = useState(null);
  const [saveStatus, setSaveStatus] = useState('idle');
  const [trainingsZiel, setTrainingsziel] = useState(() => localStorage.getItem('trainingsZiel') || 'kraft');
  const [zielDefaults, setZielDefaults] = useState(() => {
    let saved = null;
    try { saved = JSON.parse(localStorage.getItem('zielDefaults')); } catch { /* ignore */ }
    return {
      kraft: { sets: saved?.kraft?.sets || 4, reps: saved?.kraft?.reps || 5 },
      hypertrophie: { sets: saved?.hypertrophie?.sets || 3, reps: saved?.hypertrophie?.reps || 10 },
    };
  });
  // Soll-Einstellung (Sätze/Wdh) für den aktiven Modus — eigener Inline-Edit mit
  // Entwurf/Speichern/Abbrechen, getrennt von der Modus-Auswahl (Kraft/MZ-Buttons).
  const [zielEditOffen, setZielEditOffen] = useState(false);
  const [zielEditDraft, setZielEditDraft] = useState({ sets: 1, reps: 1 });

  // ── Übung hinzufügen (Feature 2) ──
  const [zeigeUebungPicker, setZeigeUebungPicker] = useState(false);
  const [uebungSuche, setUebungSuche] = useState('');
  const [neueUebungModus, setNeueUebungModus] = useState(false);
  const [neueUebungName, setNeueUebungName] = useState('');
  const [neueUebungGruppe, setNeueUebungGruppe] = useState('unterkörper');
  const [neueUebungEssentials, setNeueUebungEssentials] = useState(false);
  const [neueUebungFehler, setNeueUebungFehler] = useState('');

  // ── Essentials-Einstellungen (Feature 3) ──
  const [zeigeEssentialsEditor, setZeigeEssentialsEditor] = useState(false);

  // ── Offene-Session / Auto-Save Zustand ──
  const [sessionId, setSessionId] = useState(null);
  const [ksuIds, setKsuIds] = useState({});           // uebungId -> kraft_session_uebung_id
  const [abgeschlossen, setAbgeschlossen] = useState({}); // uebungId -> bool
  const [sessionAbgeschlossen, setSessionAbgeschlossen] = useState(false);
  const [flashMap, setFlashMap] = useState({});        // 'uebungId-idx' -> 'saving'|'saved'|'error'
  const offeneSessionGeladen = useRef(false);
  const offeneSessionData = useRef(null);
  const sessionIdRef = useRef(null);
  const ksuIdsRef = useRef({});
  const setsRef = useRef({});
  const autosaveTimers = useRef({});
  const ensurePromises = useRef({});     // uebungId -> laufendes ensureUebungGespeichert-Promise (Race-Guard)
  const kraftOriginale = useRef({});     // 'uebungId-idx' -> Kraft-Gewicht zum Zeitpunkt des Toggles nach MZ

  useEffect(() => { sessionIdRef.current = sessionId; }, [sessionId]);
  useEffect(() => { ksuIdsRef.current = ksuIds; }, [ksuIds]);
  useEffect(() => { setsRef.current = sets; }, [sets]);

  // Ändert NUR den Anzeige-Modus (+ Soll-Sätze/Wdh über den trainingsZiel-Effekt in
  // UebungCard). Kein Schreibzugriff auf sets, kein Autosave — Gewichte werden nur noch
  // als Vorschlag angezeigt (siehe berechneVorschlag in UebungCard) und nur durch
  // expliziten Klick übernommen.
  //
  // Beim Wechsel nach MZ wird das aktuelle (Kraft-)Gewicht je Satz einmalig als
  // "Kraft-Original" gemerkt. Der Vorschlag in UebungCard wird nur angezeigt, solange
  // das Feld noch exakt diesen Wert zeigt — so entsteht nie ein Vorschlag, der vom
  // bereits reduzierten MZ-Gewicht aus weiterrechnet (Drift durch Mehrfach-Anwendung).
  function switchZiel(z) {
    if (z === trainingsZiel) return;
    setTrainingsziel(z);
    localStorage.setItem('trainingsZiel', z);

    if (z === 'hypertrophie') {
      selected.forEach(uebungId => {
        (sets[uebungId] || []).forEach((s, idx) => {
          if (parseFloat(s.gewicht) > 0) {
            kraftOriginale.current[uebungId + '-' + idx] = s.gewicht;
          }
        });
      });
    }
    // Beim Zurückwechseln zu Kraft bleibt die Ref bewusst bestehen — sie liefert dort
    // den Kraft-Original-Hinweis (siehe berechneVorschlag in UebungCard). Gelöscht wird
    // sie nur beim Laden einer neuen offenen Session (applyOffeneSessionWennBereit).
  }

  function updateZielDefault(ziel, feld, wert) {
    const num = Math.max(1, parseInt(wert) || 1);
    setZielDefaults(zd => {
      const next = { ...zd, [ziel]: { ...zd[ziel], [feld]: num } };
      localStorage.setItem('zielDefaults', JSON.stringify(next));
      return next;
    });
  }

  function zielEditOeffnen() {
    setZielEditDraft({ sets: zielDefaults[trainingsZiel].sets, reps: zielDefaults[trainingsZiel].reps });
    setZielEditOffen(true);
  }
  function zielEditSpeichern() {
    updateZielDefault(trainingsZiel, 'sets', zielEditDraft.sets);
    updateZielDefault(trainingsZiel, 'reps', zielEditDraft.reps);
    setZielEditOffen(false);
  }
  function zielEditAbbrechen() {
    setZielEditOffen(false);
  }

  useEffect(() => {
    apiFetch(API + '/gym/uebungen').then(r => r.json()).then(setUebungen).catch(() => {});
    apiFetch(API + '/gym/balance').then(r => r.json()).then(setBalance).catch(() => {});
    apiFetch(API + '/gym/offene-session').then(r => r.json()).then(d => { offeneSessionData.current = d.session; applyOffeneSessionWennBereit(); }).catch(() => {});
    // AI sofort beim Öffnen laden
    loadAI();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Übungsliste kommt asynchron rein — Zuordnung per Name erst wenn beides da ist
  useEffect(() => {
    if (uebungen.length > 0) applyOffeneSessionWennBereit();
  }, [uebungen]); // eslint-disable-line react-hooks/exhaustive-deps

  function applyOffeneSessionWennBereit() {
    const session = offeneSessionData.current;
    if (offeneSessionGeladen.current || !session || uebungen.length === 0) return;
    offeneSessionGeladen.current = true;

    setSessionId(session.id);
    sessionIdRef.current = session.id;
    setSessionAbgeschlossen(session.abgeschlossen);
    setGruppenTab(session.gruppe === 'gym_a' ? 'unterkörper' : session.gruppe === 'gym_c' ? 'essentials' : 'oberkörper');

    // Die geladenen Gewichte stehen in dem Modus, in dem die Session zuletzt gespeichert
    // wurde (DB) — nicht zwangsläufig im Modus, den localStorage noch von einem früheren
    // Gerät/Tab kennt. Bei Abweichung die DB gewinnen lassen, sonst rechnet der nächste
    // Toggle die frisch geladenen Zahlen fälschlich um (falsche Richtung/Basis).
    if (session.trainingsziel && session.trainingsziel !== trainingsZiel) {
      setTrainingsziel(session.trainingsziel);
      localStorage.setItem('trainingsZiel', session.trainingsziel);
    }
    // Alte, session-fremde Kraft-Originale verwerfen — sonst könnte ein Toggle später
    // versehentlich einen Ref-Eintrag aus einer vorherigen Seiten-Session wiederverwenden.
    kraftOriginale.current = {};

    const newSelected = new Set();
    const newSets = {};
    const newKsuIds = {};
    const newAbgeschlossen = {};
    Object.entries(session.savedUebungen).forEach(([name, data]) => {
      const u = uebungen.find(x => x.name === name);
      if (!u) return;
      newSelected.add(u.id);
      newKsuIds[u.id] = data.ksuId;
      newAbgeschlossen[u.id] = data.abgeschlossen;
      newSets[u.id] = data.saetze.map(s => ({
        id: s.id,
        gewicht: s.gewicht != null ? String(parseFloat(s.gewicht)) : '',
        reps: s.wiederholungen != null ? String(s.wiederholungen) : '',
        rpe: s.rpe != null ? String(s.rpe) : '',
      }));
    });
    setSelected(newSelected);
    setSets(newSets);
    setsRef.current = newSets;
    setKsuIds(newKsuIds);
    ksuIdsRef.current = newKsuIds;
    setAbgeschlossen(newAbgeschlossen);
  }

  function loadAI() {
    setAiLoading(true);
    setAiEmpf(null);
    apiFetch(API + '/gym/ai-empfehlung')
      .then(r => r.json())
      .then(d => { setAiEmpf(d.empfehlung || (d.error ? 'KI nicht verfügbar.' : 'Keine Empfehlung.')); setAiLoading(false); })
      .catch(() => { setAiEmpf('Verbindungsfehler.'); setAiLoading(false); });
  }

  function toggleUebung(id) {
    setSelected(s => {
      const n = new Set(s);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  }

  function updateSets(id, newSets, changedIdx) {
    setSets(s => ({ ...s, [id]: newSets }));
    if (changedIdx != null) scheduleAutosave(id, changedIdx);
  }

  function buildInitialSets(uebung) {
    const anzahl = trainingsZiel === 'kraft' ? zielDefaults.kraft.sets : (uebung.default_sets || zielDefaults.hypertrophie.sets);
    const reps = uebung.einheit === 's'
      ? (uebung.default_reps || 30)
      : (trainingsZiel === 'kraft' ? zielDefaults.kraft.reps : (uebung.default_reps || zielDefaults.hypertrophie.reps));
    const defaultGew = uebung.empfehlung?.empfehlung || uebung.letzteSession?.gewicht || '';
    return Array.from({ length: anzahl }, () => ({ gewicht: defaultGew ? String(defaultGew) : '', reps: String(reps), rpe: '' }));
  }

  function uebungZuSessionHinzufuegen(u) {
    setSelected(s => new Set(s).add(u.id));
    setSets(s => (s[u.id]?.length ? s : { ...s, [u.id]: buildInitialSets(u) }));
    setZeigeUebungPicker(false);
    setUebungSuche('');
    setNeueUebungModus(false);
    setNeueUebungName('');
    setNeueUebungFehler('');
  }

  async function neueUebungAnlegen() {
    if (!neueUebungName.trim()) { setNeueUebungFehler('Name fehlt'); return; }
    try {
      const r = await apiFetch(API + '/gym/uebungen', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: neueUebungName.trim(), muskelgruppe: neueUebungGruppe, essentials: neueUebungEssentials }),
      }).then(res => res.json());
      if (!r.ok) { setNeueUebungFehler(r.error || 'Anlegen fehlgeschlagen'); return; }
      const neu = { ...r.uebung, letzteSession: null, verlauf: [], empfehlung: null, gewicht_verhaeltnis: null };
      setUebungen(u => [...u, neu]);
      uebungZuSessionHinzufuegen(neu);
    } catch (e) {
      setNeueUebungFehler('Verbindungsfehler');
    }
  }

  async function toggleEssentialsFlag(u) {
    const neu = !u.essentials;
    setUebungen(us => us.map(x => x.id === u.id ? { ...x, essentials: neu } : x));
    try {
      const r = await apiFetch(API + '/gym/uebungen/' + u.id, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ essentials: neu }),
      }).then(res => res.json());
      if (!r.ok) throw new Error(r.error);
    } catch (e) {
      setUebungen(us => us.map(x => x.id === u.id ? { ...x, essentials: !neu } : x));
    }
  }

  function getGruppe() {
    if (gruppenTab === 'essentials') return 'gym_c';
    return gruppenTab === 'unterkörper' ? 'gym_a' : 'gym_b';
  }

  function setFlash(key, status) {
    setFlashMap(f => ({ ...f, [key]: status }));
    if (status === 'saved' || status === 'error') {
      setTimeout(() => setFlashMap(f => ({ ...f, [key]: undefined })), 1200);
    } else if (status === 'adjusted') {
      setTimeout(() => setFlashMap(f => ({ ...f, [key]: undefined })), 1800);
    }
  }

  // Legt die kraft_session_uebungen-Zeile (+ ggf. die Session) an, falls noch nicht geschehen.
  // Gibt die ksuId zurück (oder null, wenn keine Sätze vorhanden sind).
  //
  // Race-Guard: wenn mehrere Sätze derselben Übung fast gleichzeitig autosaven (z.B. weil
  // switchZiel() mehrere Sätze auf einmal anpasst), dürfen nicht mehrere parallele Aufrufe
  // jeweils eine eigene kraft_session_uebungen-Zeile (und ggf. sogar eigene Session) anlegen.
  // Der ksuIdsRef-Check allein reicht nicht, weil er erst NACH dem await gesetzt wird — bis
  // dahin sehen alle parallelen Aufrufe noch "nicht vorhanden". Das laufende Promise wird
  // deshalb selbst gecacht, sodass alle Aufrufe für dieselbe uebungId auf dasselbe Ergebnis warten.
  async function ensureUebungGespeichert(uebungId) {
    const bestehend = ksuIdsRef.current[uebungId];
    if (bestehend) return bestehend;

    if (ensurePromises.current[uebungId]) return ensurePromises.current[uebungId];

    const promise = (async () => {
      const currentSets = setsRef.current[uebungId] || [];
      if (!currentSets.length) return null;

      const uebung = uebungen.find(u => u.id === uebungId);
      const sid = sessionIdRef.current;
      const body = sid
        ? { session_id: sid, trainingsziel: trainingsZiel, uebung: { name: uebung.name, uebung_id: uebung.id, sets: currentSets } }
        : { datum: localDate(), gruppe: getGruppe(), trainingsziel: trainingsZiel, uebung: { name: uebung.name, uebung_id: uebung.id, sets: currentSets } };

      const r = await apiFetch(API + '/gym/uebung-speichern', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
      }).then(res => res.json());
      if (!r.ok) throw new Error(r.error || 'Speichern fehlgeschlagen');

      setSessionId(r.session_id);
      sessionIdRef.current = r.session_id;
      setKsuIds(k => ({ ...k, [uebungId]: r.ksu_id }));
      ksuIdsRef.current = { ...ksuIdsRef.current, [uebungId]: r.ksu_id };

      const updatedSets = currentSets.map((st, i) => {
        const saved = r.saetze.find(sv => sv.satz_nr === i + 1);
        return saved ? { ...st, id: saved.id } : st;
      });
      setSets(s => ({ ...s, [uebungId]: updatedSets }));
      setsRef.current = { ...setsRef.current, [uebungId]: updatedSets };

      return r.ksu_id;
    })();

    ensurePromises.current[uebungId] = promise;
    try {
      return await promise;
    } finally {
      delete ensurePromises.current[uebungId];
    }
  }

  function scheduleAutosave(uebungId, idx) {
    const key = uebungId + '-' + idx;
    clearTimeout(autosaveTimers.current[key]);
    autosaveTimers.current[key] = setTimeout(() => persistSatz(uebungId, idx), 700);
  }

  async function persistSatz(uebungId, idx) {
    const key = uebungId + '-' + idx;
    try {
      setFlash(key, 'saving');
      const ksuId = await ensureUebungGespeichert(uebungId);
      if (!ksuId) { setFlash(key, undefined); return; }

      const satz = (setsRef.current[uebungId] || [])[idx];
      if (!satz) { setFlash(key, undefined); return; }

      if (satz.id) {
        const r = await apiFetch(API + '/gym/satz/' + satz.id, {
          method: 'PUT', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ gewicht: satz.gewicht, reps: satz.reps, rpe: satz.rpe }),
        }).then(res => res.json());
        if (!r.ok) throw new Error(r.error);
      } else {
        const r = await apiFetch(API + '/gym/satz', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ kraft_session_uebung_id: ksuId, satz_nr: idx + 1, gewicht: satz.gewicht, reps: satz.reps, rpe: satz.rpe }),
        }).then(res => res.json());
        if (!r.ok) throw new Error(r.error);
        setSets(s => ({ ...s, [uebungId]: (s[uebungId] || []).map((st, i) => i === idx ? { ...st, id: r.id } : st) }));
      }
      setFlash(key, 'saved');
    } catch (e) {
      setFlash(key, 'error');
    }
  }

  async function deleteSatzRemote(satzId) {
    try { await apiFetch(API + '/gym/satz/' + satzId, { method: 'DELETE' }); } catch (e) { /* still removed locally */ }
  }

  async function abschliessenUebung(uebungId) {
    try {
      const ksuId = await ensureUebungGespeichert(uebungId);
      if (!ksuId) return;
      const neuerWert = !abgeschlossen[uebungId];
      await apiFetch(API + '/gym/session-uebung/' + ksuId + '/abschliessen', {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ abgeschlossen: neuerWert }),
      }).then(r => r.json());
      setAbgeschlossen(a => ({ ...a, [uebungId]: neuerWert }));
    } catch (e) { /* keine UI-Meldung nötig, Nutzer kann erneut klicken */ }
  }

  async function beendenSession() {
    setSaveStatus('saving');
    try {
      for (const uebungId of selected) {
        await ensureUebungGespeichert(uebungId);
      }
      const sid = sessionIdRef.current;
      if (!sid) { setSaveStatus('idle'); return; }

      const r = await apiFetch(API + '/gym/session-abschliessen/' + sid, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ trainingsziel: trainingsZiel }),
      }).then(res => res.json());
      if (!r.ok) throw new Error(r.error || 'Abschließen fehlgeschlagen');

      setSaveStatus('saved');
      setSessionAbgeschlossen(true);
      setTimeout(() => { setSaveStatus('idle'); onClose(); }, 1500);
    } catch (e) {
      setSaveStatus('error');
      setTimeout(() => setSaveStatus('idle'), 2000);
    }
  }

  const inGruppe = u => gruppenTab === 'essentials' ? !!u.essentials : u.muskelgruppe === gruppenTab;
  const gruppenUebungen = uebungen.filter(u => inGruppe(u));
  const andereGruppe   = uebungen.filter(u => !inGruppe(u));
  const totalSelected  = selected.size;

  const MODI = [
    { key:'kraft',        label:'Kraft',         info:'~85% 1RM', col:'#8a8adf', bg:'#1a1a3a', border:'#3a3a7a' },
    { key:'hypertrophie', label:'Muskelzuwachs', info:'~70% 1RM', col:'#4a9c6f', bg:'#0d2010', border:'#2a5c2a' },
  ];
  const aktiverModus = MODI.find(m => m.key === trainingsZiel);

  return (
    <div style={{display:'flex', flexDirection:'column', gap:12}}>

      {/* Header */}
      <div style={{display:'flex', justifyContent:'space-between', alignItems:'center'}}>
        <div style={{fontSize:16, fontWeight:700, color:'#4a9c6f'}}>Gym</div>
        <div style={{display:'flex', gap:8, alignItems:'center'}}>
          <button onClick={onClose} style={{background:'transparent', border:'1px solid #2a2a2a', borderRadius:6, color:'#666', fontSize:12, padding:'4px 12px', cursor:'pointer', fontFamily:'inherit'}}>Zurück</button>
        </div>
      </div>

      {/* KI-Empfehlung */}
      <div style={{background:'#0a160a', border:'1px solid #2a5c2a', borderRadius:8, overflow:'hidden'}}>
        <button
          onClick={loadAI} disabled={aiLoading}
          style={{width:'100%', background:'transparent', border:'none', padding:'12px 16px', color: aiLoading ? '#4a7c59' : '#5abf7a', fontSize:13, fontWeight:700, cursor:'pointer', fontFamily:'inherit', textAlign:'left', display:'flex', justifyContent:'space-between', alignItems:'center'}}
        >
          <span>{aiLoading ? 'Analysiere...' : aiEmpf ? 'KI-Empfehlung' : 'KI-Empfehlung laden'}</span>
          {!aiEmpf && !aiLoading && <span style={{fontSize:11, color:'#4a7c59'}}>tippen</span>}
        </button>
        {aiEmpf && (
          <div style={{padding:'0 16px 14px', fontSize:13, color:'#c0e0c0', lineHeight:1.7, whiteSpace:'pre-wrap'}}>
            {aiEmpf.replace(/\*\*/g, '')}
          </div>
        )}
      </div>

      {/* Muskelgruppen-Balance */}
      <MuskelBalance balance={balance}/>

      {/* Trainings-Ziel: Modus-Auswahl (oben, groß, nur Umschalten) */}
      <div style={{display:'flex', background:'#141414', borderRadius:8, padding:3, gap:3}}>
        {MODI.map(m => (
          <button key={m.key} onClick={() => switchZiel(m.key)} style={{
            flex:1, padding:'10px', borderRadius:6, cursor:'pointer', fontFamily:'inherit',
            border: '1px solid ' + (trainingsZiel === m.key ? m.border : 'transparent'),
            background: trainingsZiel === m.key ? m.bg : 'transparent',
            textAlign:'center',
          }}>
            <div style={{fontSize:13, fontWeight:700, color: trainingsZiel === m.key ? m.col : '#555'}}>{m.label}</div>
            <div style={{fontSize:9, color: trainingsZiel === m.key ? m.col : '#3a3a3a', opacity:0.85, marginTop:2}}>{m.info}</div>
          </button>
        ))}
      </div>

      {/* Soll-Einstellung (darunter, kompakt) — eigene Werte je Modus */}
      <div style={{background:'#141414', borderRadius:8, padding:'8px 12px'}}>
        {zielEditOffen ? (
          <div style={{display:'flex', flexWrap:'wrap', alignItems:'center', gap:10}}>
            <div style={{display:'flex', alignItems:'center', gap:4}}>
              <button
                onClick={() => setZielEditDraft(d => ({ ...d, sets: Math.max(1, d.sets - 1) }))}
                style={{width:36, height:36, background:'#0a0a0a', border:'1px solid #333', borderRadius:6, color:aktiverModus.col, fontSize:16, fontWeight:700, cursor:'pointer', fontFamily:'inherit', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0}}
              >−</button>
              <span style={{fontSize:13, color:'#ccc', minWidth:18, textAlign:'center'}}>{zielEditDraft.sets}</span>
              <button
                onClick={() => setZielEditDraft(d => ({ ...d, sets: Math.min(8, d.sets + 1) }))}
                style={{width:36, height:36, background:'#0a0a0a', border:'1px solid #333', borderRadius:6, color:aktiverModus.col, fontSize:16, fontWeight:700, cursor:'pointer', fontFamily:'inherit', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0}}
              >+</button>
              <span style={{fontSize:9, color:'#555'}}>Sätze</span>
            </div>
            <div style={{display:'flex', alignItems:'center', gap:4}}>
              <button
                onClick={() => setZielEditDraft(d => ({ ...d, reps: Math.max(1, d.reps - 1) }))}
                style={{width:36, height:36, background:'#0a0a0a', border:'1px solid #333', borderRadius:6, color:aktiverModus.col, fontSize:16, fontWeight:700, cursor:'pointer', fontFamily:'inherit', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0}}
              >−</button>
              <span style={{fontSize:13, color:'#ccc', minWidth:18, textAlign:'center'}}>{zielEditDraft.reps}</span>
              <button
                onClick={() => setZielEditDraft(d => ({ ...d, reps: Math.min(30, d.reps + 1) }))}
                style={{width:36, height:36, background:'#0a0a0a', border:'1px solid #333', borderRadius:6, color:aktiverModus.col, fontSize:16, fontWeight:700, cursor:'pointer', fontFamily:'inherit', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0}}
              >+</button>
              <span style={{fontSize:9, color:'#555'}}>Wdh</span>
            </div>
            <div style={{display:'flex', gap:6, marginLeft:'auto'}}>
              <button
                onClick={zielEditSpeichern} title="Speichern"
                style={{width:28, height:28, background:'#0d2010', border:'1px solid #2a5c2a', borderRadius:6, color:'#4a9c6f', fontSize:14, fontWeight:700, cursor:'pointer', fontFamily:'inherit', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0}}
              >✓</button>
              <button
                onClick={zielEditAbbrechen} title="Abbrechen"
                style={{width:28, height:28, background:'#1a0d0d', border:'1px solid #5c2a2a', borderRadius:6, color:'#cf7f50', fontSize:14, fontWeight:700, cursor:'pointer', fontFamily:'inherit', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0}}
              >✗</button>
            </div>
          </div>
        ) : (
          <div onClick={zielEditOeffnen} style={{display:'flex', alignItems:'center', justifyContent:'space-between', cursor:'pointer'}}>
            <span style={{fontSize:11, color:'#888'}}>
              Soll: {zielDefaults[trainingsZiel].sets} Sätze × {zielDefaults[trainingsZiel].reps} Wdh
            </span>
            <span style={{fontSize:13, color:aktiverModus.col}}>✎</span>
          </div>
        )}
      </div>

      {/* Status-Zeile */}
      <div style={{fontSize:11, color:'#555'}}>
        {totalSelected > 0 ? `${totalSelected} Übung${totalSelected !== 1 ? 'en' : ''} ausgewählt` : 'Übungen antippen zum Auswählen'}
      </div>

      {/* Gruppen-Tabs */}
      <div style={{display:'flex', gap:6}}>
        {[
          { key:'unterkörper', label:'Unterkörper', col:'#4a9c6f', bg:'#0d2010', border:'#2a5c2a' },
          { key:'oberkörper',  label:'Oberkörper',  col:'#5a8fd4', bg:'#0d1020', border:'#2a3a5c' },
          { key:'essentials',  label:'Essentials',  col:'#c19a3a', bg:'#201a0d', border:'#5c4a2a' },
        ].map(g => {
          const n = uebungen.filter(u => (g.key === 'essentials' ? !!u.essentials : u.muskelgruppe === g.key) && selected.has(u.id)).length;
          return (
            <button key={g.key} onClick={() => setGruppenTab(g.key)} style={{
              flex:1, background: gruppenTab===g.key ? g.bg : '#141414',
              color: gruppenTab===g.key ? g.col : '#555',
              border:'1px solid ' + (gruppenTab===g.key ? g.border : '#222'),
              borderRadius:6, padding:'8px 12px', fontSize:12, fontWeight:700, cursor:'pointer', fontFamily:'inherit',
            }}>
              {g.label}
              {n > 0 && <span style={{marginLeft:6, background:g.col, color:'#fff', borderRadius:10, padding:'0 5px', fontSize:9}}>{n}</span>}
            </button>
          );
        })}
      </div>

      {/* Übungsliste aktive Gruppe */}
      <div style={{background:'#141414', borderRadius:8, overflow:'hidden'}}>
        {gruppenUebungen.map(u => (
          <UebungCard
            key={u.id} uebung={u}
            trainingsZiel={trainingsZiel}
            zielDefaults={zielDefaults}
            isSelected={selected.has(u.id)}
            onToggle={() => toggleUebung(u.id)}
            sets={sets[u.id] || []}
            onSetsChange={(newSets, idx) => updateSets(u.id, newSets, idx)}
            abgeschlossen={!!abgeschlossen[u.id]}
            onAbschliessen={() => abschliessenUebung(u.id)}
            flashMap={flashMap}
            onDeleteSatz={deleteSatzRemote}
            kraftOriginale={kraftOriginale.current}
          />
        ))}
      </div>

      {/* Übung hinzufügen / Essentials konfigurieren */}
      <div style={{display:'flex', gap:8}}>
        <button
          onClick={() => setZeigeUebungPicker(true)}
          style={{flex:1, background:'transparent', border:'1px dashed #333', borderRadius:8, color:'#4a9c6f', fontSize:12, fontWeight:600, padding:'10px 12px', cursor:'pointer', fontFamily:'inherit'}}
        >+ Übung hinzufügen</button>
        {gruppenTab === 'essentials' && (
          <button
            onClick={() => setZeigeEssentialsEditor(true)}
            style={{background:'transparent', border:'1px dashed #333', borderRadius:8, color:'#c19a3a', fontSize:12, fontWeight:600, padding:'10px 12px', cursor:'pointer', fontFamily:'inherit'}}
          >⚙ Essentials bearbeiten</button>
        )}
      </div>

      {/* Ausgewählte aus anderer Gruppe */}
      {andereGruppe.some(u => selected.has(u.id)) && (
        <div style={{background:'#141414', borderRadius:8, overflow:'hidden'}}>
          <div style={{padding:'8px 16px', fontSize:10, color:'#555', letterSpacing:2}}>
            ANDERE ÜBUNGEN — auch ausgewählt
          </div>
          {andereGruppe.filter(u => selected.has(u.id)).map(u => (
            <UebungCard
              key={u.id} uebung={u}
              trainingsZiel={trainingsZiel}
            zielDefaults={zielDefaults}
              isSelected={true}
              onToggle={() => toggleUebung(u.id)}
              sets={sets[u.id] || []}
              onSetsChange={(newSets, idx) => updateSets(u.id, newSets, idx)}
              abgeschlossen={!!abgeschlossen[u.id]}
              onAbschliessen={() => abschliessenUebung(u.id)}
              flashMap={flashMap}
              onDeleteSatz={deleteSatzRemote}
              kraftOriginale={kraftOriginale.current}
            />
          ))}
        </div>
      )}

      {/* Training beenden */}
      {totalSelected > 0 && (
        <button
          onClick={beendenSession}
          disabled={saveStatus === 'saving'}
          style={{
            background: saveStatus === 'saved' ? '#2a5c3a' : saveStatus === 'error' ? '#5c2a2a' : '#4a7c59',
            color:'#fff', border:'none', borderRadius:8, padding:'14px',
            fontSize:15, fontWeight:700, cursor:'pointer', fontFamily:'inherit',
          }}
        >
          {saveStatus === 'saved' ? 'Training gespeichert!' : saveStatus === 'error' ? 'Fehler beim Speichern' : saveStatus === 'saving' ? 'Speichere...' : sessionAbgeschlossen ? `Training aktualisieren (${totalSelected} Übungen)` : `Training beenden (${totalSelected} Übungen)`}
        </button>
      )}

      {/* ── Übung-hinzufügen Modal (Feature 2) ── */}
      {zeigeUebungPicker && (
        <div
          onClick={() => { setZeigeUebungPicker(false); setNeueUebungModus(false); setNeueUebungFehler(''); }}
          style={{position:'fixed', inset:0, background:'rgba(0,0,0,0.7)', display:'flex', alignItems:'flex-end', justifyContent:'center', zIndex:100}}
        >
          <div onClick={e => e.stopPropagation()} style={{background:'#141414', borderRadius:'12px 12px 0 0', padding:16, width:'100%', maxWidth:480, maxHeight:'75vh', display:'flex', flexDirection:'column'}}>
            <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:10}}>
              <div style={{fontSize:14, fontWeight:700, color:'#e0e0e0'}}>{neueUebungModus ? 'Neue Übung anlegen' : 'Übung hinzufügen'}</div>
              <button onClick={() => { setZeigeUebungPicker(false); setNeueUebungModus(false); setNeueUebungFehler(''); }} style={{background:'transparent', border:'none', color:'#666', fontSize:18, cursor:'pointer', fontFamily:'inherit'}}>×</button>
            </div>

            {!neueUebungModus ? (
              <>
                <input
                  autoFocus
                  placeholder="Übung suchen..."
                  value={uebungSuche}
                  onChange={e => setUebungSuche(e.target.value)}
                  style={{background:'#1a1a1a', border:'1px solid #2a2a2a', borderRadius:6, padding:'9px 12px', color:'#e0e0e0', fontSize:13, fontFamily:'inherit', outline:'none', marginBottom:10}}
                />
                <div style={{overflowY:'auto', flex:1}}>
                  {uebungen
                    .filter(u => u.name.toLowerCase().includes(uebungSuche.trim().toLowerCase()))
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map(u => (
                      <div key={u.id} onClick={() => uebungZuSessionHinzufuegen(u)}
                        style={{display:'flex', justifyContent:'space-between', alignItems:'center', padding:'10px 8px', borderBottom:'1px solid #1c1c1c', cursor:'pointer'}}
                      >
                        <span style={{fontSize:13, color: selected.has(u.id) ? '#4a9c6f' : '#e0e0e0'}}>{u.name}{selected.has(u.id) ? ' ✓' : ''}</span>
                        <span style={{fontSize:10, color:'#555'}}>{u.muskelgruppe}{u.essentials ? ' · ESS' : ''}</span>
                      </div>
                    ))}
                  {uebungen.filter(u => u.name.toLowerCase().includes(uebungSuche.trim().toLowerCase())).length === 0 && (
                    <div style={{fontSize:12, color:'#555', padding:'10px 8px'}}>Keine Übung gefunden.</div>
                  )}
                </div>
                <button
                  onClick={() => { setNeueUebungModus(true); setNeueUebungName(uebungSuche); }}
                  style={{marginTop:10, background:'transparent', border:'1px dashed #2a3a2a', borderRadius:6, color:'#4a7c59', fontSize:12, padding:'9px 12px', cursor:'pointer', fontFamily:'inherit'}}
                >+ Neue Übung anlegen</button>
              </>
            ) : (
              <div style={{display:'flex', flexDirection:'column', gap:10}}>
                <input
                  autoFocus
                  placeholder="Name der Übung"
                  value={neueUebungName}
                  onChange={e => setNeueUebungName(e.target.value)}
                  style={{background:'#1a1a1a', border:'1px solid #2a2a2a', borderRadius:6, padding:'9px 12px', color:'#e0e0e0', fontSize:13, fontFamily:'inherit', outline:'none'}}
                />
                <div style={{display:'flex', gap:8}}>
                  {['unterkörper', 'oberkörper'].map(g => (
                    <button key={g} onClick={() => setNeueUebungGruppe(g)} style={{
                      flex:1, padding:'8px', borderRadius:6, fontSize:12, fontFamily:'inherit', cursor:'pointer',
                      border:'1px solid ' + (neueUebungGruppe === g ? '#4a7c59' : '#2a2a2a'),
                      background: neueUebungGruppe === g ? '#0d2010' : 'transparent',
                      color: neueUebungGruppe === g ? '#4a9c6f' : '#888',
                    }}>{g}</button>
                  ))}
                </div>
                <label style={{display:'flex', alignItems:'center', gap:8, fontSize:12, color:'#aaa', cursor:'pointer'}}>
                  <input type="checkbox" checked={neueUebungEssentials} onChange={e => setNeueUebungEssentials(e.target.checked)} />
                  Als Essentials-Übung markieren
                </label>
                {neueUebungFehler && <div style={{fontSize:11, color:'#cf7f50'}}>{neueUebungFehler}</div>}
                <div style={{display:'flex', gap:8}}>
                  <button onClick={() => { setNeueUebungModus(false); setNeueUebungFehler(''); }}
                    style={{flex:1, background:'transparent', border:'1px solid #2a2a2a', borderRadius:6, color:'#888', fontSize:12, padding:'10px', cursor:'pointer', fontFamily:'inherit'}}
                  >Zurück</button>
                  <button onClick={neueUebungAnlegen}
                    style={{flex:2, background:'#4a7c59', border:'none', borderRadius:6, color:'#fff', fontSize:12, fontWeight:700, padding:'10px', cursor:'pointer', fontFamily:'inherit'}}
                  >Anlegen & hinzufügen</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Essentials-Editor Modal (Feature 3) ── */}
      {zeigeEssentialsEditor && (
        <div
          onClick={() => setZeigeEssentialsEditor(false)}
          style={{position:'fixed', inset:0, background:'rgba(0,0,0,0.7)', display:'flex', alignItems:'flex-end', justifyContent:'center', zIndex:100}}
        >
          <div onClick={e => e.stopPropagation()} style={{background:'#141414', borderRadius:'12px 12px 0 0', padding:16, width:'100%', maxWidth:480, maxHeight:'75vh', display:'flex', flexDirection:'column'}}>
            <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:10}}>
              <div style={{fontSize:14, fontWeight:700, color:'#e0e0e0'}}>Essentials-Übungen</div>
              <button onClick={() => setZeigeEssentialsEditor(false)} style={{background:'transparent', border:'none', color:'#666', fontSize:18, cursor:'pointer', fontFamily:'inherit'}}>×</button>
            </div>
            <div style={{fontSize:11, color:'#555', marginBottom:8}}>Auswahl der Übungen, die im Essentials-Tab erscheinen.</div>
            <div style={{overflowY:'auto', flex:1}}>
              {[...uebungen].sort((a, b) => a.name.localeCompare(b.name)).map(u => (
                <label key={u.id} style={{display:'flex', alignItems:'center', gap:10, padding:'9px 8px', borderBottom:'1px solid #1c1c1c', cursor:'pointer'}}>
                  <input type="checkbox" checked={!!u.essentials} onChange={() => toggleEssentialsFlag(u)} />
                  <span style={{fontSize:13, color:'#e0e0e0', flex:1}}>{u.name}</span>
                  <span style={{fontSize:10, color:'#555'}}>{u.muskelgruppe}</span>
                </label>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
